import BaseButton from 'core/buttons/base-button';
import ConditionalLink from 'core/conditional-link';
import formatNumber from 'utils/format-number';
import Image from 'core/image';
import insuranceLogoMapper from 'utils/insurance-logo-mapper/insurance-logo-mapper';
import ItemChildWrapper from './item-child-wrapper';
import PriceLabelItemIsBestHOC from 'components/landing/tables/components/price-tag/price-tag-item-is-best-hoc';
import PriceTagItem from 'components/landing/tables/components/price-tag/price-tag-item';
import PropTypes from 'prop-types';
import styles from 'styles/LandingTablesComponents.module.scss';
import Top5DataItem from './data-item';
import useRatingAlias from 'hooks/useGetRatingAlias';
import { useTranslations } from 'next-intl';
import variables from 'styles/variables.module.scss';

/**
 * # Top5Item
 * Top 5 item that renders all the quotation information
 * @component
 * @param { Array.<object> } headers - Array of object with the Headers information and codes to select the coverage code to be rendered
 * @param { Array.<object> } columnData - Array of object with the data related tot he quotation information
 * @param { culture } culture -  culture information to map through the image mapper
 * @returns
 */
const Top5Item = ({ headers, columnData, culture }) => {
    const { getRatingAlias } = useRatingAlias();
    const t = useTranslations();

    return (
        <div
            className={`
                ${styles.column_item__wrapper}
                ${styles.top_5__column__wrapper}
            `}
        >
            <ItemChildWrapper>
                <p className={styles.top_5_title}>{columnData.company_name}</p>
            </ItemChildWrapper>
            <ItemChildWrapper>
                <ConditionalLink
                    link={columnData.page_full_url}
                    target="_blank"
                    rel="noreferrer"
                >
                    <Image
                        src={insuranceLogoMapper(
                            columnData.company_code,
                            culture,
                            'colored'
                        )}
                        alt={`${columnData.company_name} Logo`}
                        // width={110}
                        // height={36.6}
                        style={{ width: '110px', height: '36.6px' }}
                    />
                </ConditionalLink>
            </ItemChildWrapper>
            <ItemChildWrapper>
                <p>
                    {getRatingAlias(columnData.global_rating)}{' '}
                    <span>{columnData.global_rating}</span>
                </p>
            </ItemChildWrapper>
            <ItemChildWrapper>
                <PriceLabelItemIsBestHOC
                    value={formatNumber(Math.round(columnData.insurance_price_discount))}
                    isBest={columnData.isBest}
                />
            </ItemChildWrapper>
            <ItemChildWrapper>
                <PriceTagItem
                    value={formatNumber(Math.round(columnData.price))}
                    lineThrough
                    backgroundColor={variables.whiteColor}
                />
                <PriceTagItem
                    value={columnData.discount_percent}
                    tagType="percentage"
                    color={variables.whiteColor}
                    backgroundColor={variables.primaryColor}
                    fontSize=".9rem"
                />
            </ItemChildWrapper>
            <ItemChildWrapper>
                <Top5DataItem disableCheck data={columnData.family_name} />
            </ItemChildWrapper>
            {[...headers].splice(6, 11).map((item, index) => {
                if (!item) {
                    return null;
                }
                const cover = columnData.coverage_list.find((co) => {
                    const COVER_CODE = co.code.match(/^([^-]*)/);
                    return item.code === COVER_CODE[0];
                });
                return (
                    <ItemChildWrapper key={item.code}>
                        <Top5DataItem data={cover && cover.value} />
                    </ItemChildWrapper>
                );
            })}
            <ItemChildWrapper>
                <BaseButton
                    label={t('table.items.more-info')}
                    href={t('quoter')}
                    divClass={styles.button__wrapper}
                    buttonClass={`${styles.button} ${styles.pink}`}
                />
            </ItemChildWrapper>
        </div>
    );
};

Top5Item.propTypes = {
    columnData: PropTypes.object,
    headers: PropTypes.array,
    culture: PropTypes.string.isRequired
};

export default Top5Item;
